import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Timesedler (2026-10-23): én side pr. chauffør med start og slut hver dag,
 * normaltimer, overtid og tillæg - det overenskomsten kræver på lønsedlen.
 * Kun timer (ingen kroner) - en egen lønlinje i kr (fx udlæg) står med sit
 * beløb, som den er sat på dagen. Data fra /payroll/period. Kolonnen "Fravær" er
 * fraværstimer der betales som en normal dag (fx syg); ferie via feriepenge
 * står kun som bemærkning.
 */
const INK = [15, 23, 42];
const MUTED = [100, 116, 139];
const WD = ["søn", "man", "tir", "ons", "tor", "fre", "lør"];

const h = (m) => (m ? (m / 60).toFixed(2).replace(".", ",") : "");
const paidAbsence = (x) => (x.absence?.pay === "normal" ? x.absence.minutes || 0 : 0);
// 2026-10-25: Hver vagt for sig (to vagter så før ud som én lang vagt, og
// timerne kunne ikke regnes efter), og den stemplede tid ved siden af den
// afrundede, når de er forskellige.
const shiftTxt = (b) => `${b.start}–${b.end}${b.end_next_day ? " +1" : ""}`;
// Stemplingerne, som de er (to stemplinger med et hul imellem er én vagt med pause).
const stampTxt = (x) => (x.entries || []).filter((e) => e.start_time).map((e) =>
  `${e.start_time}–${e.end_time || "…"}${e.end_next_day ? " +1" : ""}${e.slice_type === "pause" ? " pause" : ""}`).join("\n");
// Hvem der har rettet og hvorfor (før stod der bare "Rettet").
function corrections(x) {
  const out = [];
  for (const e of x.entries || []) {
    const admin = e.created_by_role === "logistics_admin" || e.created_by_role === "super_admin";
    // 2026-10-25: Den tid, der stod før rettelsen (stemplingen), står med.
    const was = (o) => (o ? ` (var ${o.start_time}–${o.end_time || "åben"}${o.end_next_day ? " +1" : ""})` : "");
    if (e.edited_by_admin) out.push(`Rettet af ${e.edited_by_admin_name || "kontoret"}${was(e.admin_original)}${e.reason ? `: ${e.reason}` : ""}`);
    else if (e.manual && admin) out.push(`Tilføjet af ${e.created_by_name || "kontoret"}${e.reason ? `: ${e.reason}` : ""}`);
    else if (e.manual) out.push(`Stemplet manuelt${e.reason ? `: ${e.reason}` : ""}`);
    if (e.edited_by_driver) out.push(`Rettet af chaufføren${was(e.driver_original)}`);
  }
  if (x.adjustment?.note) out.push(x.adjustment.note);
  if (!out.length && x.flags.some((f) => f.code === "corrected")) out.push("Rettet");
  return [...new Set(out)];
}

// 2026-10-25: Firmaets egne lønlinjer (fx udetillæg, søndagstillæg) stod ikke på
// timesedlen - chaufføren kunne ikke se dem, selv om de kommer med i lønfilen.
const qtyTxt = (q, unit) => `${unit === "stk" ? String(q).replace(".", ",") : Number(q).toFixed(2).replace(".", ",")} ${unit}`;
const extraTxt = (x) => (x.extra || []).map((l) => `${l.name}: ${qtyTxt(l.qty, l.unit)}${l.note && !l.auto ? ` (${l.note})` : ""}`);

function isoWeek(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const y0 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - y0) / 86400000 + 1) / 7);
}

export function buildPaySheetPdf({ period, drivers }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const ap = period.approval;
  const status = ap
    ? (ap.trial ? "Prøve - ikke godkendt" : `Godkendt ${new Date(ap.first_approved_at || ap.approved_at).toLocaleDateString("da-DK")}`)
    : "Ikke godkendt";
  // 2026-10-25: Rettet efter godkendelsen - så passer timesedlen ikke med
  // periodens lønseddel. Sig, hvor forskellen er (eller kommer) med.
  const afterNote = (d) => {
    if (!ap || ap.trial) return "";
    const sent = (ap.corrected_in || {})[d.id] || [];
    const pending = (ap.changed_ids || []).includes(d.id);
    if (!sent.length && !pending) return "";
    return "Rettet efter godkendelsen: timesedlen viser tiderne, som de er nu. Forskellen "
      + (sent.length ? `er betalt med lønnen for ${sent.join(" og ")}` : "")
      + (sent.length && pending ? ", og den nyeste rettelse " : "")
      + (pending ? "kommer med i næste lønperiode" : "") + "."
      + (ap.rerate?.reason ? ` Reglerne for perioden er rettet bagefter: ${ap.rerate.reason}.` : "");
  };
  let first = true;
  for (const d of drivers) {
    const days = d.days.filter((x) => x.blocks.length || x.absence || (x.extra || []).length);
    if (!days.length) continue;
    if (!first) doc.addPage();
    first = false;
    doc.setTextColor(...INK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(`Timeseddel – ${d.name}`, 12, 14);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    const scheds = period.rules?.schedules || [];
    const own = scheds.find((x) => x.id === d.profile.schedule_id);
    const std = scheds.find((x) => x.id === period.rules?.default_schedule) || scheds[0];
    const noSched = d.profile.employment === "afloeser" && !d.profile.schedule_id;
    const schedTxt = d.profile.employment === "ingen" ? "" : noSched ? "uden fast skema" : `skema «${(own || std)?.name || "standard"}»`;
    doc.text(`${period.company_name} · Periode ${period.label} · Lønnummer ${d.profile.employee_no || "–"} · ${d.employment_label}${schedTxt ? `, ${schedTxt}` : ""} · ${status}`, 12, 20);

    const body = [];
    let week = null;
    let wt = [0, 0, 0, 0, 0, 0];
    const flush = () => {
      if (week === null) return;
      body.push([{ content: `Uge ${week}`, colSpan: 5, styles: { fontStyle: "bold" } },
        ...wt.map((v) => ({ content: h(v) || "–", styles: { fontStyle: "bold" } })), ""]);
    };
    for (const x of days) {
      const dt = new Date(x.date + "T12:00:00");
      const wk = isoWeek(dt);
      if (wk !== week) { flush(); week = wk; wt = [0, 0, 0, 0, 0, 0]; }
      const ln = x.lines || {};
      const nums = [ln.timer || ln.overtimer || 0, ln.ot1 || 0, ln.ot2 || 0, ln.aften || 0, ln.nat || 0, paidAbsence(x)];
      wt = wt.map((v, i) => v + nums[i]);
      const ab = x.absence;
      // Fravær i fraværskolonnen (før i bemærkningen): typen - og timerne, når de betales.
      const abTxt = ab ? `${ab.label}${ab.part === "rest" ? " (resten)" : ""}${paidAbsence(x) ? ` ${h(paidAbsence(x))}` : ""}${ab.counts === false ? (noSched ? " (ingen timer)" : " (fridag - tæller ikke)") : ""}${ab.pay === "unpaid" && ab.minutes ? ` (fradrag ${h(ab.minutes)} t)` : ""}` : "";
      const stamped = stampTxt(x);
      const same = stamped === x.blocks.map(shiftTxt).join("\n");
      const note = [x.holiday, ...extraTxt(x), ...corrections(x), ...x.flags.filter((f) => f.code === "short_stamp").map((f) => f.text)].filter(Boolean).join(" · ");
      body.push([`${dt.getDate()}.${dt.getMonth() + 1}`, WD[dt.getDay()], x.blocks.map(shiftTxt).join("\n"),
        same ? "" : stamped, x.blocks.length && x.pause_min ? `${x.pause_min}` : "",
        ...nums.slice(0, 5).map(h), abTxt, note]);
    }
    flush();
    const t = d.totals || {};
    body.push([{ content: "I alt", colSpan: 5, styles: { fontStyle: "bold" } },
      ...[t.timer || t.overtimer || 0, t.ot1, t.ot2, t.aften, t.nat, days.reduce((s, x) => s + paidAbsence(x), 0)]
        .map((v) => ({ content: h(v || 0) || "–", styles: { fontStyle: "bold" } })), ""]);
    autoTable(doc, {
      startY: 25,
      head: [["Dato", "Dag", "Arbejdstid", "Stemplet", "Pause (min)", "Timer i alt", "Heraf overtid 1", "Heraf overtid 2", "Aften (tillæg)", "Nat (tillæg)", "Fravær", "Bemærkning"]],
      body,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 1.4, textColor: INK, lineColor: [226, 232, 240] },
      headStyles: { fillColor: [241, 245, 249], textColor: INK, fontStyle: "bold" },
      columnStyles: { 2: { cellWidth: 26 }, 3: { cellWidth: 26, textColor: MUTED }, 10: { cellWidth: 30 }, 11: { cellWidth: 52 } },
      margin: { left: 12, right: 12 },
    });
    let y = doc.lastAutoTable.finalY + 6;
    const after = afterNote(d);
    if (after) {
      doc.setFontSize(9);
      doc.setTextColor(180, 83, 9);
      doc.text(after, 12, Math.min(y, 196), { maxWidth: W - 24 });
      y += 5;
    }
    // 2026-10-25: Timer til afspadsering (tidskonto) stod ikke på timesedlen -
    // chaufføren så færre timer uden at vide hvorfor.
    const bank = (t.afsp_timer || 0);
    const bankPrem = (t.ot1_afsp || 0) + (t.ot2_afsp || 0);
    if (bank || bankPrem) {
      doc.setFontSize(9);
      doc.setTextColor(...INK);
      doc.text(`Til afspadsering i perioden: ${h(bank) || "0,00"} timer${bankPrem ? ` + overtidstillæg ${h(bankPrem)} timer` : ""} (står ikke i kolonnerne ovenfor - lønsystemet fører saldoen).`, 12, Math.min(y, 196));
      y += 5;
    }
    const sums = {};
    days.forEach((x) => (x.extra || []).forEach((l) => { const k = `${l.name}|${l.unit}`; sums[k] = (sums[k] || 0) + Number(l.qty || 0); }));
    const sumTxt = Object.entries(sums).filter(([, v]) => Math.round(v * 100)).map(([k, v]) => { const [n, u] = k.split("|"); return `${n} ${qtyTxt(Math.round(v * 100) / 100, u)}`; });
    if (sumTxt.length) {
      doc.setFontSize(9);
      doc.setTextColor(...INK);
      doc.text(`Lønlinjer i perioden: ${sumTxt.join(" · ")} (står under Bemærkning ud for dagen).`, 12, Math.min(y, 196), { maxWidth: W - 24 });
      y += 5;
    }
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    const r = period.rules || {};
    const rnd = r.rounding?.interval ? `Arbejdstiden er afrundet til ${r.rounding.interval} min efter firmaets regler (den stemplede tid står ved siden af, når den afviger). ` : "";
    const ot = d.profile.employment === "ingen" ? "" : noSched
      ? `Overtid regnes efter firmaets standardskema «${std?.name || "standard"}», da du ikke har et fast skema. `
      : "Overtid er tiden ud over din normale dag efter skemaet. ";
    doc.text(`${rnd}Pausen er trukket fra. ${ot}Timer står i decimaltal (7,50 = 7 t 30 min). "Timer i alt" omfatter overtiden - overtid og tillæg er timer oven i timelønnen. `
      + "Er noget forkert, så sig det til din arbejdsgiver.", 12, Math.min(y, 196), { maxWidth: W - 24 });
  }
  if (first) {
    doc.setFontSize(12);
    doc.text("Ingen registreringer i perioden.", 12, 20);
  }
  return doc;
}
